import { useDreams } from "../contexts/DreamsContext";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Sparkle, Compass, Star, Crown, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const features = [
  "Descreve a tua vida de sonho — a IA sugere 8-10 sonhos concretos",
  "Cada sonho com data, valor, prémio e consequência",
  "Foto do sonho e do prémio para manter a visão viva",
  "Plano de acção gerado por IA, com tarefas calendarizadas",
  "Dream Board visual e top de Sonhadores",
  "Emails diários motivacionais + alerta de tarefas pendentes",
];

export default function DreamsLanding() {
  const { me, trialEligible, startTrial } = useDreams();
  const userName = me?.user?.NomeCompleto?.split(" ")[0] || "Sonhador";
  const trialExpirado = !!me?.trial_expirado;
  const [starting, setStarting] = useState(false);
  const [erro, setErro] = useState(null);

  const begin = async () => {
    setStarting(true); setErro(null);
    try {
      await startTrial();
    } catch (e) {
      setErro(e?.response?.data?.detail || "Não foi possível activar o período gratuito.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-12 pb-12">
      {/* Hero */}
      <section className="pt-8 pb-2">
        {trialExpirado && (
          <div className="mb-6 rounded-2xl border border-amber-500/40 bg-amber-500/5 p-5" data-testid="trial-expired-banner">
            <p className="font-semibold text-[#0F1829]">O teu período gratuito de 3 meses terminou.</p>
            <p className="text-sm text-[#4B5563] mt-1">
              Os teus sonhos, tarefas e pontos continuam guardados. Escolhe um plano para voltares a ter acesso —
              desde 5€/mês, sem fidelização.
            </p>
            <Link
              to="/dreams/subscribe"
              data-testid="trial-expired-cta"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F1829] text-white font-semibold text-sm hover:bg-[#1b2942] transition"
            >
              Escolher o meu plano <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        )}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#6F4F1D]/10 border border-[#6F4F1D]/25 text-[11px] text-[#6F4F1D] font-medium mb-6" data-testid="landing-badge">
          <Sparkle size={12} weight="fill" /> Sistema de Acompanhamento Virtual
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight max-w-3xl">
          Olá, <span className="text-[#6F4F1D]">{userName}</span>.
          <br />
          <span className="text-white/60">O que sonhas</span>
          <br />
          <span className="bg-gradient-to-r from-amber-300 via-fuchsia-400 to-violet-400 bg-clip-text text-transparent">tornar realidade?</span>
        </h1>
        <p className="text-lg text-[#4B5563] max-w-xl mt-6 leading-relaxed">
          O IHTP Dreams transforma a tua visão de vida ideal numa lista concreta de sonhos
          — com prazos, prémios e um plano de acção feito à tua medida pela IA.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {trialEligible ? (
            <button
              onClick={begin}
              disabled={starting}
              data-testid="landing-cta-trial"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm hover:shadow-[0_0_40px_rgba(245,158,11,0.4)] transition-all disabled:opacity-60"
            >
              {starting ? "A activar…" : "Começar grátis — 3 meses"}
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            <Link
              to="/dreams/subscribe"
              data-testid="landing-cta-subscribe"
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm hover:shadow-[0_0_40px_rgba(245,158,11,0.4)] transition-all"
            >
              Começar agora — desde 5€/mês
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
          <Link
            to="/dreams/vision"
            data-testid="landing-cta-vision"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#E6E1D6] text-sm font-medium hover:bg-white"
          >
            Experimenta sem subscrever
          </Link>
        </div>
        {trialEligible && (
          <p className="mt-3 text-xs text-[#4B5563]" data-testid="landing-trial-note">
            3 meses de acesso completo, sem cartão e sem compromisso. No fim escolhes se queres continuar.
          </p>
        )}
        {erro && <p className="mt-3 text-xs text-rose-500" data-testid="landing-trial-error">{erro}</p>}
      </section>

      {/* Steps */}
      <section className="grid sm:grid-cols-3 gap-5">
        {[
          { icon: Compass, title: "Define", text: "Descreve a tua vida ideal numa frase." },
          { icon: Sparkle, title: "Gera",   text: "A IA propõe sonhos concretos com prazos." },
          { icon: Star,    title: "Concretiza", text: "Plano de acção, tarefas, pontos e prémios." },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#E6E1D6] bg-white/[0.02] backdrop-blur p-6 hover:border-[#E6E1D6] transition-colors">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6F4F1D]/20 to-fuchsia-500/20 flex items-center justify-center mb-4">
              <s.icon size={20} weight="fill" className="text-[#6F4F1D]" />
            </div>
            <h3 className="text-lg font-semibold">{s.title}</h3>
            <p className="text-sm text-[#4B5563] mt-1">{s.text}</p>
          </div>
        ))}
      </section>

      {/* Features list */}
      <section className="rounded-3xl border border-[#E6E1D6] bg-gradient-to-br from-violet-900/20 to-fuchsia-900/10 p-8">
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Crown size={22} weight="fill" className="text-[#6F4F1D]" />
          O que vais ter
        </h2>
        <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm text-[#0F1829]">
              <CheckCircle size={16} weight="fill" className="text-[#6F4F1D] mt-0.5 shrink-0" />
              {f}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
